// Reads a month, a project or a quarter from the address, so a link such as
// /effort?period=2026-09&project_key=SRL opens that view. Anything that does not look right is ignored.

export function periodParam(params: URLSearchParams): string | null {
  const v = params.get('period');
  return v && /^\d{4}-(0[1-9]|1[0-2])$/.test(v) ? v : null;
}

export function projectParam(params: URLSearchParams): string | null {
  const v = params.get('project_key');
  return v && /^[A-Za-z0-9_]{1,30}$/.test(v) ? v : null;
}

export function quarterParam(params: URLSearchParams, key = 'quarter'): string | null {
  const v = params.get(key);
  return v && /^\d{4}-Q[1-4]$/.test(v) ? v : null;
}

// ─── Months, flags and tabs ──────────────────────────────────────

/** This month as "YYYY-MM", in the browser's clock. */
export function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** "2026-01" stepped by -1 is "2025-12". */
export function shiftMonth(value: string, delta: number): string {
  const [y, m] = value.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** A month from the address that is not in the future; anything else is this month. */
export function clampMonth(value: string | null, current: string = currentMonth()): string {
  return value && value <= current ? value : current;
}

/** A yes/no choice kept as 1 in the address ("Only mine"). */
export function flagParam(params: URLSearchParams, key: string): boolean {
  return params.get(key) === '1';
}

/** A tab from the address, only when it is one the person may have; otherwise null. */
export function tabParam<T extends string>(params: URLSearchParams, allowed: readonly T[], key = 'tab'): T | null {
  const v = params.get(key);
  return v !== null && (allowed as readonly string[]).includes(v) ? (v as T) : null;
}

/** A year from the address, "2026". */
export function yearParam(params: URLSearchParams, key = 'year'): string | null {
  const v = params.get(key);
  return v && /^\d{4}$/.test(v) ? v : null;
}

/** A whole number such as a sprint id from the address, or null. */
export function idParam(params: URLSearchParams, key: string): number | null {
  const v = params.get(key);
  return v && /^\d{1,9}$/.test(v) && Number(v) > 0 ? Number(v) : null;
}
